import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./ProjectMembership.css";
import { API_BASE } from "../../../ApiUrl";
import ProjectTabs from "./ProjectTabs";

const MEMBERSHIPS_API_URL = `${API_BASE}/project-memberships/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const PROJECTS_API_URL = `${API_BASE}/projects/`;
const USERS_API_URL = `${API_BASE}/users/`;
const ROLES_API_URL = `${API_BASE}/roles/`;

const emptyMembership = {
  id: null,
  project: "",
  user: "",
  role: "",
  scopes: [],
  scopes_input: "",
  effective_from: "",
  effective_to: ""
};

const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");

const AddProjectMembership = () => {
  const navigate = useNavigate();

  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyMembership }]);
  const [tenantId, setTenantId] = useState(null);
  const [userId, setUserId] = useState(null);

  const [projectsList, setProjectsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [rolesList, setRolesList] = useState([]);

  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ----------------------------------------------------------
  // GET TENANT + USER ID
  // ----------------------------------------------------------

  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
    if (tenantUser?.user_id) setUserId(Number(tenantUser.user_id));
  }, []);

  // ----------------------------------------------------------
  // FETCH MEMBERSHIPS
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) fetchMemberships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchMemberships = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["project_memberships"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.project_memberships) {
        rows = result.data.project_memberships;
      } else if (result?.project_memberships) {
        rows = result.project_memberships;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setMemberships(rows);
    } catch (error) {
      console.error("Error fetching project memberships:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load memberships",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading memberships."
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // LOOKUPS
  // ----------------------------------------------------------

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      const tenantUser = getTenantUser();

      try {
        const response = await axios.get(PROJECTS_API_URL, {
          params: tenantUser?.tenant_id
            ? { tenant: tenantUser.tenant_id }
            : undefined
        });

        const result = response.data;
        const data = result?.data
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.results || [];

        const filtered = tenantUser?.tenant_id
          ? data.filter(
              (p) => String(p.tenant) === String(tenantUser.tenant_id)
            )
          : data;

        setProjectsList(filtered);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      const tenantUser = getTenantUser();

      try {
        const response = await axios.get(USERS_API_URL, {
          params: tenantUser?.tenant_id
            ? { tenant: tenantUser.tenant_id }
            : undefined
        });

        const result = response.data;
        const data = result?.data
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.results || [];

        const filtered = tenantUser?.tenant_id
          ? data.filter(
              (u) => String(u.tenant) === String(tenantUser.tenant_id)
            )
          : data;

        setUsersList(filtered);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);

      try {
        const response = await axios.get(ROLES_API_URL);
        const result = response.data;
        const data = result?.data
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.results || [];

        setRolesList(data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // ----------------------------------------------------------
  // FORM HANDLERS
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [name]: value } : m))
    );
  };

  const handleScopeInputChange = (index, e) => {
    const { value } = e.target;
    setFormData((prev) =>
      prev.map((m, i) => (i === index ? { ...m, scopes_input: value } : m))
    );
  };

  const addScopeChip = (index) => {
    setFormData((prev) =>
      prev.map((m, i) => {
        if (i !== index) return m;
        const raw = (m.scopes_input || "").trim();
        if (!raw) return { ...m, scopes_input: "" };
        if (m.scopes.includes(raw)) return { ...m, scopes_input: "" };
        return { ...m, scopes: [...m.scopes, raw], scopes_input: "" };
      })
    );
  };

  const handleScopeKeyDown = (index, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addScopeChip(index);
    } else if (e.key === "Backspace" && !e.target.value) {
      setFormData((prev) =>
        prev.map((m, i) => {
          if (i !== index) return m;
          if (!m.scopes.length) return m;
          return { ...m, scopes: m.scopes.slice(0, -1) };
        })
      );
    }
  };

  const removeScopeChip = (index, scopeValue) => {
    setFormData((prev) =>
      prev.map((m, i) =>
        i === index
          ? { ...m, scopes: m.scopes.filter((s) => s !== scopeValue) }
          : m
      )
    );
  };

  // ----------------------------------------------------------
  // ROW HANDLERS
  // ----------------------------------------------------------

  const handleAdd = () => {
    setFormData([{ ...emptyMembership }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyMembership }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (m) => {
    let scopesArr = [];
    if (Array.isArray(m.scopes)) {
      scopesArr = m.scopes.map((s) => String(s));
    } else if (typeof m.scopes === "string") {
      try {
        const parsed = JSON.parse(m.scopes);
        if (Array.isArray(parsed)) {
          scopesArr = parsed.map((s) => String(s));
        }
      } catch {
        scopesArr = m.scopes
          ? m.scopes.split(",").map((s) => s.trim()).filter(Boolean)
          : [];
      }
    }

    setFormData([
      {
        id: m.id,
        project: m.project ? String(m.project) : "",
        user: m.user ? String(m.user) : "",
        role: m.role ? String(m.role) : "",
        scopes: scopesArr,
        scopes_input: "",
        effective_from: toDateInput(m.effective_from),
        effective_to: toDateInput(m.effective_to)
      }
    ]);
    setEditingId(m.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (m) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Membership?",
      text: "This will permanently delete this project membership.",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${MEMBERSHIPS_API_URL}${m.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Membership deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchMemberships();
    } catch (error) {
      console.error("Error deleting membership:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the membership."
      });
    }
  };

  // ----------------------------------------------------------
  // SUBMIT
  // ----------------------------------------------------------

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const buildFlatPayload = (m) => ({
    project: Number(m.project),
    user: m.user ? Number(m.user) : null,
    role: Number(m.role),
    scopes: m.scopes.map((s) => s.trim()).filter(Boolean),
    effective_from: m.effective_from || null,
    effective_to: m.effective_to || null,
    tenant: tenantId,
    assigned_by: userId
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId || !userId) {
      Swal.fire({
        icon: "error",
        title: "Session missing",
        text: "Tenant ID or User ID is missing."
      });
      return;
    }

    const committed = formData.map((m) => {
      const pending = (m.scopes_input || "").trim();
      if (pending && !m.scopes.includes(pending)) {
        return { ...m, scopes: [...m.scopes, pending], scopes_input: "" };
      }
      return { ...m, scopes_input: "" };
    });

    const activeRows = committed.filter((m) => m.project);

    if (!activeRows.length) {
      Swal.fire({
        icon: "warning",
        title: "No valid rows",
        text: "Please select a project for at least one membership."
      });
      return;
    }

    const CONFIG = {
      headers: { "Content-Type": "application/json" }
    };

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildFlatPayload(activeRows[0]);
        await axios.put(`${MEMBERSHIPS_API_URL}${editingId}/`, payload, CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const payload = {
          tenant: tenantId,
          assigned_by: userId,
          memberships: activeRows.map((m) => ({
            project: Number(m.project),
            user: m.user ? Number(m.user) : null,
            role: Number(m.role),
            scopes: m.scopes.map((s) => s.trim()).filter(Boolean),
            effective_from: m.effective_from || null,
            effective_to: m.effective_to || null
          }))
        };

        await axios.post(MEMBERSHIPS_API_URL, payload, CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} membership${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyMembership }]);

      await fetchMemberships();
    } catch (error) {
      console.error("Error saving memberships:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyMembership }]);
  };

  // ----------------------------------------------------------
  // LOOKUP HELPERS
  // ----------------------------------------------------------

  const getProjectName = (id) => {
    const p = projectsList.find((x) => String(x.id) === String(id));
    return p?.project_name || p?.name || (id ? `Project ${id}` : "-");
  };

  const getUserName = (id) => {
    const u = usersList.find((x) => String(x.id) === String(id));
    return (
      u?.full_name ||
      u?.username ||
      u?.email ||
      (id ? `User ${id}` : "-")
    );
  };

  const getRoleName = (id) => {
    const r = rolesList.find((x) => String(x.id) === String(id));
    return r?.name || r?.role_name || r?.code || (id ? `Role ${id}` : "-");
  };

  const getScopesArray = (m) => {
    if (Array.isArray(m.scopes)) return m.scopes;
    if (typeof m.scopes === "string") {
      try {
        const parsed = JSON.parse(m.scopes);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return m.scopes.split(",").map((s) => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-membership-page">
          <ProjectTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="tenant-membership-page-header">
                <div>
                  <h2>Project Memberships</h2>
                </div>

                <button
                  type="button"
                  className="tenant-membership-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Membership
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="tenant-membership-list-card">
                {loading ? (
                  <div className="tenant-membership-loading">
                    Loading memberships...
                  </div>
                ) : memberships.length === 0 ? (
                  <div className="tenant-membership-empty">
                    <div className="tenant-membership-empty-icon">+</div>
                    <h3>No Memberships Found</h3>
                    <p>
                      There are no project memberships available for the
                      current tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-membership-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Membership
                    </button>
                  </div>
                ) : (
                  <div className="tenant-membership-table-wrapper">
                    <table className="tenant-membership-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>User</th>
                          <th>Role</th>
                          <th>Scopes</th>
                          <th>Effective From</th>
                          <th>Effective To</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {memberships.map((m) => (
                          <tr key={m.id}>
                            <td>
                              <strong>{getProjectName(m.project)}</strong>
                            </td>

                            <td>{getUserName(m.user)}</td>

                            <td>
                              <span className="tenant-membership-role-pill">
                                {getRoleName(m.role)}
                              </span>
                            </td>

                            <td>
                              <div className="tenant-membership-scope-pills">
                                {getScopesArray(m).length === 0 ? (
                                  <span className="tenant-membership-muted">
                                    -
                                  </span>
                                ) : (
                                  getScopesArray(m).map((s, idx) => (
                                    <span
                                      key={idx}
                                      className="tenant-membership-scope-pill"
                                    >
                                      {s}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td>{m.effective_from || "-"}</td>
                            <td>{m.effective_to || "-"}</td>

                            <td>
                              <div className="tenant-membership-actions">
                                <button
                                  type="button"
                                  className="tenant-membership-edit-btn"
                                  onClick={() => handleEdit(m)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-membership-delete-btn"
                                  onClick={() => handleDelete(m)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="tenant-membership-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Membership"
                      : "Create Memberships"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="tenant-membership-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((m, index) => (
                  <div
                    className="tenant-membership-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-membership-form-body">
                      <div className="tenant-membership-record-header">
                        <h4>Membership {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-membership-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-membership-form-grid">
                        <div className="tenant-membership-form-group">
                          <label>Project</label>
                          <select
                            name="project"
                            value={m.project}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingProjects}
                            required
                          >
                            <option value="">
                              {loadingProjects
                                ? "Loading..."
                                : "Select project"}
                            </option>
                            {projectsList.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.project_name ||
                                  p.name ||
                                  `Project ${p.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-membership-form-group">
                          <label>User</label>
                          <select
                            name="user"
                            value={m.user}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingUsers}
                          >
                            <option value="">
                              {loadingUsers ? "Loading..." : "Select user"}
                            </option>
                            {usersList.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.full_name ||
                                  u.username ||
                                  u.email ||
                                  `User ${u.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-membership-form-group">
                          <label>Role</label>
                          <select
                            name="role"
                            value={m.role}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingRoles}
                            required
                          >
                            <option value="">
                              {loadingRoles ? "Loading..." : "Select role"}
                            </option>
                            {rolesList.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name ||
                                  r.role_name ||
                                  r.code ||
                                  `Role ${r.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-membership-form-group">
                          <label>Effective From</label>
                          <input
                            type="date"
                            name="effective_from"
                            value={m.effective_from}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-membership-form-group">
                          <label>Effective To</label>
                          <input
                            type="date"
                            name="effective_to"
                            value={m.effective_to}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-membership-form-group tenant-membership-form-group--full">
                          <label>Scopes</label>
                          <div className="tenant-membership-scope-chips">
                            {m.scopes.length === 0 && !m.scopes_input ? (
                              <span className="tenant-membership-scope-loading">
                                Type a scope and press Enter
                              </span>
                            ) : null}

                            {m.scopes.map((scope) => (
                              <span
                                className="tenant-membership-scope-chip tenant-membership-scope-chip--active"
                                key={scope}
                              >
                                <span>{scope}</span>
                                <button
                                  type="button"
                                  className="tenant-membership-scope-chip-remove"
                                  onClick={() =>
                                    removeScopeChip(index, scope)
                                  }
                                  aria-label={`Remove ${scope}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}

                            <input
                              type="text"
                              className="tenant-membership-scope-input"
                              value={m.scopes_input}
                              onChange={(e) =>
                                handleScopeInputChange(index, e)
                              }
                              onKeyDown={(e) =>
                                handleScopeKeyDown(index, e)
                              }
                              onBlur={() => addScopeChip(index)}
                              placeholder={
                                m.scopes.length ? "" : "e.g. SITE_SUPERVISION"
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-membership-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Membership
                  </button>
                )}

                <div className="tenant-membership-form-actions">
                  <button
                    type="button"
                    className="tenant-membership-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-membership-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Membership"
                      : "Save Memberships"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddProjectMembership;